import { GuardSpinner } from '@/components/GuardSpinner';
import { hasSessionsWriteAccess, useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/hooks/useSettings';
import { OnboardingFlow } from '@/pages/onboarding/OnboardingFlow';
import { formValuesFromProfile } from '@/pages/onboarding/onboardingForm';
import { useOnboardingStatus } from '@/services/onboarding/useOnboardingStatus';
import { statusAccountId } from '@/services/statusGate/useStatusGate';
import { Box, Text } from '@careflux/primitives';
import { useEffect } from 'react';
import { useLocation } from 'wouter';

/** First-run onboarding, also reachable from Settings ("Breyta prófíl") to edit saved answers. */
const OnboardingPage = () => {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  // Completion is rejected under impersonation and without sessions:write, so don't offer the form.
  const canComplete = !!user && !user.impersonating && hasSessionsWriteAccess(user);
  const { data, isError, isFetchedAfterMount } = useOnboardingStatus({
    accountId: statusAccountId(user),
    enabled: canComplete,
  });
  const { features, isLoading: isSettingsLoading, isReady: isSettingsReady } = useSettings({ enabled: canComplete });

  useEffect(() => {
    if (user && !canComplete) navigate('/', { replace: true });
  }, [user, canComplete, navigate]);

  // The form reads its default values once, so wait for this mount's own status request:
  // answers cached from an earlier visit (or another tab's edit) could be stale.
  // isFetchedAfterMount also settles on failure, where the query keeps that cached data,
  // so a failed fetch drops it and starts from current settings.
  if (!canComplete || !isFetchedAfterMount || isSettingsLoading) return <GuardSpinner />;
  // Without settings the form would prefill defaults and completion would overwrite saved ones.
  if (!isSettingsReady) {
    return (
      <Box display="flex" align="center" justify="center" height="screen" background="background">
        <Text color="destructive">Ekki tókst að sækja stillingar. Reyndu aftur síðar.</Text>
      </Box>
    );
  }
  const status = isError ? undefined : data;

  return (
    <OnboardingFlow
      initialValues={formValuesFromProfile(status?.answers ?? null, features)}
      isEditMode={!!status?.completed}
      isFirstRun={status?.completed === false}
    />
  );
};

export default OnboardingPage;
